import type { Vec2, Ring, PolygonWithHoles, AABB, OBB, Segment } from './types';

export function signedArea(r: Ring): number {
  let area = 0;
  const n = r.length;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    area += r[i]![0] * r[j]![1];
    area -= r[j]![0] * r[i]![1];
  }
  return area / 2;
}

export function ensureCCW(r: Ring): Ring {
  return signedArea(r) < 0 ? [...r].reverse() : r;
}

export function ensureCW(r: Ring): Ring {
  return signedArea(r) > 0 ? [...r].reverse() : r;
}

export function pointInRing(p: Vec2, r: Ring): boolean {
  let inside = false;
  const n = r.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = r[i]![0],
      yi = r[i]![1];
    const xj = r[j]![0],
      yj = r[j]![1];
    if (yi > p[1] !== yj > p[1] && p[0] < ((xj - xi) * (p[1] - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
}

export function pointInPolygon(p: Vec2, poly: PolygonWithHoles): boolean {
  if (!pointInRing(p, poly.outer)) return false;
  for (const hole of poly.holes) {
    if (pointInRing(p, hole)) return false;
  }
  return true;
}

export function ringBounds(r: Ring): AABB {
  if (r.length === 0) {
    return { minX: 0, minY: 0, maxX: 0, maxY: 0 };
  }
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;
  for (const p of r) {
    if (p[0] < minX) minX = p[0];
    if (p[1] < minY) minY = p[1];
    if (p[0] > maxX) maxX = p[0];
    if (p[1] > maxY) maxY = p[1];
  }
  return { minX, minY, maxX, maxY };
}

export function polygonArea(p: PolygonWithHoles): number {
  let area = Math.abs(signedArea(p.outer));
  for (const hole of p.holes) {
    area -= Math.abs(signedArea(hole));
  }
  return area;
}

export function centroid(r: Ring): Vec2 {
  if (r.length === 0) return [0, 0];
  let cx = 0,
    cy = 0;
  let area = 0;
  const n = r.length;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const cross = r[i]![0] * r[j]![1] - r[j]![0] * r[i]![1];
    cx += (r[i]![0] + r[j]![0]) * cross;
    cy += (r[i]![1] + r[j]![1]) * cross;
    area += cross;
  }
  area /= 2;
  if (Math.abs(area) < 1e-10) {
    let sx = 0,
      sy = 0;
    for (const p of r) {
      sx += p[0];
      sy += p[1];
    }
    return [sx / r.length, sy / r.length];
  }
  return [cx / (6 * area), cy / (6 * area)];
}

function cross(o: Vec2, a: Vec2, b: Vec2): number {
  return (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
}

export function convexHull(pts: readonly Vec2[]): Ring {
  if (pts.length < 3) return [...pts];

  const sorted = [...pts].sort((a, b) => (a[0] === b[0] ? a[1] - b[1] : a[0] - b[0]));

  const lower: Vec2[] = [];
  for (const p of sorted) {
    while (lower.length >= 2 && cross(lower[lower.length - 2]!, lower[lower.length - 1]!, p) <= 0) {
      lower.pop();
    }
    lower.push(p);
  }

  const upper: Vec2[] = [];
  for (let i = sorted.length - 1; i >= 0; i--) {
    const p = sorted[i]!;
    while (upper.length >= 2 && cross(upper[upper.length - 2]!, upper[upper.length - 1]!, p) <= 0) {
      upper.pop();
    }
    upper.push(p);
  }

  lower.pop();
  upper.pop();
  return [...lower, ...upper];
}

function rotatingCalipers(hull: Ring): { angle: number; width: number; height: number; center: Vec2 } {
  const n = hull.length;
  if (n < 3) {
    const bounds = ringBounds(hull);
    return {
      angle: 0,
      width: bounds.maxX - bounds.minX,
      height: bounds.maxY - bounds.minY,
      center: [(bounds.minX + bounds.maxX) / 2, (bounds.minY + bounds.maxY) / 2],
    };
  }

  let minArea = Infinity;
  let bestAngle = 0;
  let bestWidth = 0;
  let bestHeight = 0;
  let bestCenter: Vec2 = [0, 0];

  for (let i = 0; i < n; i++) {
    const p1 = hull[i]!;
    const p2 = hull[(i + 1) % n]!;

    const dx = p2[0] - p1[0];
    const dy = p2[1] - p1[1];
    const len = Math.sqrt(dx * dx + dy * dy);
    if (len < 1e-10) continue;

    const ux = dx / len;
    const uy = dy / len;
    const vx = -uy;
    const vy = ux;

    let minU = Infinity,
      maxU = -Infinity;
    let minV = Infinity,
      maxV = -Infinity;

    for (const p of hull) {
      const u = p[0] * ux + p[1] * uy;
      const v = p[0] * vx + p[1] * vy;
      if (u < minU) minU = u;
      if (u > maxU) maxU = u;
      if (v < minV) minV = v;
      if (v > maxV) maxV = v;
    }

    const width = maxU - minU;
    const height = maxV - minV;
    const area = width * height;

    if (area < minArea) {
      minArea = area;
      bestAngle = Math.atan2(uy, ux);
      bestWidth = width;
      bestHeight = height;
      const cu = (minU + maxU) / 2;
      const cv = (minV + maxV) / 2;
      bestCenter = [cu * ux + cv * vx, cu * uy + cv * vy];
    }
  }

  return { angle: bestAngle, width: bestWidth, height: bestHeight, center: bestCenter };
}

export function minAreaOBB(r: Ring): OBB {
  const hull = convexHull(r);
  const { angle, width, height, center } = rotatingCalipers(hull);

  const ux = Math.cos(angle);
  const uy = Math.sin(angle);

  return {
    center,
    axisU: [ux, uy],
    halfU: width / 2,
    halfV: height / 2,
  };
}

export function obbCorners(o: OBB): readonly [Vec2, Vec2, Vec2, Vec2] {
  const { center, axisU, halfU, halfV } = o;
  const vx = -axisU[1];
  const vy = axisU[0];

  const ux = axisU[0] * halfU;
  const uy = axisU[1] * halfU;
  const vxs = vx * halfV;
  const vys = vy * halfV;

  return [
    [center[0] - ux - vxs, center[1] - uy - vys],
    [center[0] + ux - vxs, center[1] + uy - vys],
    [center[0] + ux + vxs, center[1] + uy + vys],
    [center[0] - ux + vxs, center[1] - uy + vys],
  ];
}

export function distancePointSegment(p: Vec2, s: Segment): number {
  const { a, b } = s;
  const ab: Vec2 = [b[0] - a[0], b[1] - a[1]];
  const ap: Vec2 = [p[0] - a[0], p[1] - a[1]];
  const lenSq = ab[0] * ab[0] + ab[1] * ab[1];

  if (lenSq < 1e-10) {
    return Math.sqrt(ap[0] * ap[0] + ap[1] * ap[1]);
  }

  const t = Math.max(0, Math.min(1, (ap[0] * ab[0] + ap[1] * ab[1]) / lenSq));
  const closest: Vec2 = [a[0] + t * ab[0], a[1] + t * ab[1]];
  const dx = p[0] - closest[0];
  const dy = p[1] - closest[1];
  return Math.sqrt(dx * dx + dy * dy);
}

export function segmentIntersection(
  s: Segment,
  t: Segment
): { readonly point: Vec2; readonly tS: number; readonly tT: number } | null {
  const { a: a1, b: b1 } = s;
  const { a: a2, b: b2 } = t;

  const d1x = b1[0] - a1[0];
  const d1y = b1[1] - a1[1];
  const d2x = b2[0] - a2[0];
  const d2y = b2[1] - a2[1];

  const cross = d1x * d2y - d1y * d2x;
  if (Math.abs(cross) < 1e-10) return null;

  const dx = a2[0] - a1[0];
  const dy = a2[1] - a1[1];

  const tS = (dx * d2y - dy * d2x) / cross;
  const tT = (dx * d1y - dy * d1x) / cross;

  if (tS < 0 || tS > 1 || tT < 0 || tT > 1) return null;

  return {
    point: [a1[0] + tS * d1x, a1[1] + tS * d1y],
    tS,
    tT,
  };
}
